import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  PhoneIphone as PhoneIcon,
  Tablet as TabletIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import { DEVICES_QUERY, REVOKE_DEVICE_MUTATION } from '../services/graphql';
import { getDeviceId } from '../utils/deviceId';
import { useAuth } from '../contexts/AuthContext';

interface Device {
  id: string;
  deviceId: string;
  label: string | null;
  userAgent: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  verifiedAt: string | null;
  isCurrent: boolean;
}

/**
 * Parse user agent to get a friendly device name
 */
function getDeviceLabel(userAgent: string | null): { label: string; type: 'desktop' | 'mobile' | 'tablet' } {
  if (!userAgent) {
    return { label: 'Unknown Device', type: 'desktop' };
  }

  const ua = userAgent.toLowerCase();

  // Check for mobile
  if (ua.includes('iphone') || (ua.includes('android') && ua.includes('mobile'))) {
    if (ua.includes('iphone')) {
      return { label: 'iPhone', type: 'mobile' };
    }
    return { label: 'Android Phone', type: 'mobile' };
  }

  // Check for tablet
  if (ua.includes('ipad') || (ua.includes('android') && !ua.includes('mobile'))) {
    if (ua.includes('ipad')) {
      return { label: 'iPad', type: 'tablet' };
    }
    return { label: 'Android Tablet', type: 'tablet' };
  }

  // Desktop browsers
  if (ua.includes('chrome') && !ua.includes('edg')) {
    if (ua.includes('mac')) return { label: 'Chrome on Mac', type: 'desktop' };
    if (ua.includes('windows')) return { label: 'Chrome on Windows', type: 'desktop' };
    if (ua.includes('linux')) return { label: 'Chrome on Linux', type: 'desktop' };
    return { label: 'Chrome', type: 'desktop' };
  }

  if (ua.includes('firefox')) {
    if (ua.includes('mac')) return { label: 'Firefox on Mac', type: 'desktop' };
    if (ua.includes('windows')) return { label: 'Firefox on Windows', type: 'desktop' };
    if (ua.includes('linux')) return { label: 'Firefox on Linux', type: 'desktop' };
    return { label: 'Firefox', type: 'desktop' };
  }

  if (ua.includes('safari') && !ua.includes('chrome')) {
    return { label: 'Safari on Mac', type: 'desktop' };
  }

  if (ua.includes('edg')) {
    if (ua.includes('mac')) return { label: 'Edge on Mac', type: 'desktop' };
    if (ua.includes('windows')) return { label: 'Edge on Windows', type: 'desktop' };
    return { label: 'Edge', type: 'desktop' };
  }

  return { label: 'Unknown Browser', type: 'desktop' };
}

function getDeviceIcon(type: 'desktop' | 'mobile' | 'tablet') {
  switch (type) {
    case 'mobile':
      return <PhoneIcon />;
    case 'tablet':
      return <TabletIcon />;
    default:
      return <ComputerIcon />;
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export default function Devices() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const currentDeviceId = getDeviceId();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [deviceToRevoke, setDeviceToRevoke] = useState<Device | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data, loading, refetch } = useQuery(DEVICES_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [revokeDeviceMutation, { loading: revoking }] = useMutation(REVOKE_DEVICE_MUTATION);

  const devices: Device[] = data?.devices || [];

  // Mark current device
  const devicesWithCurrent = devices.map((device) => ({
    ...device,
    isCurrent: device.deviceId === currentDeviceId,
  }));

  const handleRevokeClick = (device: Device) => {
    setDeviceToRevoke(device);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!deviceToRevoke) return;

    setError('');
    setSuccess('');

    const isRevokingCurrentDevice = deviceToRevoke.deviceId === currentDeviceId;

    try {
      const { data: result } = await revokeDeviceMutation({
        variables: { deviceId: deviceToRevoke.deviceId },
      });

      if (result?.revokeDevice?.success) {
        if (isRevokingCurrentDevice) {
          // Revoking current device - logout immediately
          await logout();
          navigate('/signin');
        } else {
          setSuccess('Device revoked successfully');
          refetch();
        }
      } else {
        setError(result?.revokeDevice?.message || 'Failed to revoke device');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke device');
    } finally {
      setRevokeDialogOpen(false);
      setDeviceToRevoke(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() => navigate('/settings')}
          aria-label="back to settings"
          edge="start"
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Trusted Devices
        </Typography>
      </Box>
      <Typography color="text.secondary" paragraph sx={{ ml: 6 }}>
        These devices have been verified and can access your vault without additional verification.
        If you don't recognize a device, revoke it immediately.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper elevation={0}>
        {loading ? (
          <List>
            {[1, 2, 3].map((i) => (
              <ListItem key={i} divider>
                <ListItemIcon>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="40%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : devicesWithCurrent.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No trusted devices yet. When you verify a new device during login, it will appear here.
            </Typography>
          </Box>
        ) : (
          <List>
            {devicesWithCurrent.map((device, index) => {
              const { label, type } = getDeviceLabel(device.userAgent);
              const displayLabel = device.label || label;

              return (
                <ListItem
                  key={device.id}
                  divider={index < devicesWithCurrent.length - 1}
                  sx={{
                    bgcolor: device.isCurrent ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemIcon>{getDeviceIcon(type)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{displayLabel}</Typography>
                        {device.isCurrent && (
                          <Chip
                            size="small"
                            label="Current"
                            color="primary"
                            icon={<CheckCircleIcon />}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        Last active: {formatDate(device.lastSeenAt)}
                        {device.verifiedAt && (
                          <> · Verified: {formatDate(device.verifiedAt)}</>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="revoke"
                      onClick={() => handleRevokeClick(device)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Tip:</strong> If you don't recognize a device, revoke it and change your master password.
          Revoking a device means you'll need to verify with a code next time you sign in from that device.
        </Typography>
      </Box>

      {/* Revoke confirmation dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {deviceToRevoke?.deviceId === currentDeviceId && (
            <WarningIcon color="warning" />
          )}
          Revoke Device?
        </DialogTitle>
        <DialogContent>
          {deviceToRevoke?.deviceId === currentDeviceId ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Warning:</strong> You are about to revoke your current device. You will be logged out immediately and will need to verify this device again on your next sign-in.
            </Alert>
          ) : null}
          <DialogContentText>
            Are you sure you want to revoke "{deviceToRevoke?.label || getDeviceLabel(deviceToRevoke?.userAgent || null).label}"?
            {deviceToRevoke?.deviceId !== currentDeviceId && (
              <> You'll need to verify with a code next time you sign in from this device.</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            color="error"
            variant="contained"
            disabled={revoking}
          >
            {revoking ? 'Revoking...' : deviceToRevoke?.deviceId === currentDeviceId ? 'Revoke & Logout' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

